import type { ModalContent } from './types';

import { useTranslation } from 'react-i18next';

import { useCountdown } from '../../hooks/use-countdown';

import { InfoModal } from '@/components/InfoModal/constants';
import { useCancelVerification } from '@/hooks/use-cancel-verification';
import { useAppContext } from '@/providers/app-provider/context';

/**
 * Return content for InfoModal by modal view type from app state
 * @param handleClose handler for closing modal
 * @param isModelOpen is modal open
 * @returns content properties
 */
export function useInfoModalContent(handleClose: () => void, isModelOpen: boolean): ModalContent {
  const { t } = useTranslation();
  const cancelVerification = useCancelVerification();

  const {
    state: { customerData, infoModal },
  } = useAppContext();

  const showSessionExpiration = infoModal === InfoModal.SESSION_EXPIRED;
  const { unverifiedUrl = null } = customerData?.redirect || {};

  function cancelVerificationWithClose() {
    cancelVerification(showSessionExpiration ? false : true);
    handleClose();
  }

  /*
   * Countdown starts when:
   * 1. InfoModal is open
   * 2. RedirectUrl is provided
   * 3. ViewType is 'INFO_MODAL.SESSION_EXPIRED'
   */

  const { countdown } = useCountdown({ enabled: isModelOpen && !!unverifiedUrl && showSessionExpiration });

  if (countdown === 0) {
    cancelVerification();
  }

  if (infoModal === InfoModal.CANCEL_VERIFICATION) {
    return {
      heading: t('modal.cancelVerification.heading'),
      text: t('modal.cancelVerification.text'),
      primaryButton: {
        text: t('modal.cancelVerification.primaryButton'),
        onClick: handleClose,
        testId: 'btn_continue_verif',
      },
      secondaryButton: {
        text: t('modal.cancelVerification.secondaryButton'),
        onClick: () => cancelVerificationWithClose(),
        testId: 'btn_cancel_verif',
      },
    };
  }

  // INFO_MODAL.SESSION_EXPIRED
  return {
    heading: t('modal.sessionExpired.heading'),
    text: t('modal.sessionExpired.text'),
    primaryButton: {
      text: t('modal.sessionExpired.primaryButton'),
      onClick: () => cancelVerificationWithClose(),
      testId: 'btn_reload',
    },
  };
}
